import { useEffect, useRef, type RefObject } from 'react';

const NEAR_BOTTOM_PX = 64;

function scrollToBottom(el: HTMLElement, behavior: ScrollBehavior) {
  requestAnimationFrame(() => {
    el.scrollTo({ top: el.scrollHeight, behavior });
  });
}

/** Keeps a scrollable chat panel pinned to the bottom when new content appears. */
export function useChatPanelScroll(
  containerRef: RefObject<HTMLElement | null>,
  contentKey: string
) {
  const stickToBottomRef = useRef(true);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateStickiness = () => {
      stickToBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
    };

    el.addEventListener('scroll', updateStickiness, { passive: true });
    updateStickiness();

    return () => el.removeEventListener('scroll', updateStickiness);
  }, [containerRef]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    stickToBottomRef.current = true;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior: ScrollBehavior = reducedMotion ? 'auto' : 'smooth';
    scrollToBottom(el, behavior);

    const followBottom = () => {
      if (stickToBottomRef.current) {
        scrollToBottom(el, 'auto');
      }
    };

    const mutationObserver = new MutationObserver(followBottom);
    mutationObserver.observe(el, { childList: true, subtree: true, characterData: true });

    const resizeObserver = new ResizeObserver(followBottom);
    resizeObserver.observe(el);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [containerRef, contentKey]);
}
